import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import useModalEscape from '../../hooks/useModalEscape';
import scrollbar from '../../styles/scrollbar';
import LayoutPicker from './LayoutPicker';
import LanguagePicker from '../LanguagePicker';
import { APP_VERSION } from '../../version';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const MODE_BLURBS = {
  easy: 'settings.modeBlurb.easy',
  normal: 'settings.modeBlurb.normal',
  hardcore: 'settings.modeBlurb.hardcore',
  custom: 'settings.modeBlurb.custom',
};

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,6,3,0.68)',
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  modal: {
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '14px',
    padding: '20px 24px 24px',
    maxWidth: '560px',
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '22px',
    // A centred fixed overlay clips rather than scrolls, and a short viewport
    // (a landscape phone) runs out of room well before this list does.
    maxHeight: '80vh',
    overflowY: 'auto',
    ...scrollbar,
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 20px 50px rgba(0,0,0,0.55)',
    ].join(','),
    '@media (max-width: 620px)': {
      padding: '16px 16px 20px',
      gap: '18px',
    },
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  title: {
    margin: 0,
    color: '#f5e6c8',
    fontSize: '19px',
    fontWeight: 700,
  },
  closeBtn: {
    width: '44px',
    height: '44px',
    flexShrink: 0,
    background: 'none',
    border: 'none',
    color: '#9c8168',
    fontSize: '17px',
    cursor: 'pointer',
    lineHeight: 1,
    fontFamily: 'inherit',
    borderRadius: '9px',
    '&:hover': { color: '#f0dcc0' },
    '&:active': { background: 'rgba(255,255,255,0.06)' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  sectionTitle: {
    fontFamily: MONO,
    color: '#c4a070',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
  },
  nicknameInput: {
    width: '100%',
    minHeight: '52px',
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '9px',
    color: '#f5e6c8',
    padding: '12px 16px',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color .15s ease',
    '&:focus': { borderColor: '#8b4513' },
  },
  text: {
    color: '#d9c3a2',
    fontSize: '14px',
    lineHeight: 1.6,
    margin: 0,
  },
  // A row that leads somewhere: value on the left, chevron on the right.
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    width: '100%',
    minHeight: '60px',
    padding: '12px 16px',
    boxSizing: 'border-box',
    background: '#3a2416',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: '#cbb493',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 1px 0 rgba(255,255,255,0.08)',
      'inset 0 -3px 0 rgba(0,0,0,0.35)',
    ].join(','),
    transition: 'background .12s ease, color .12s ease',
    '&:hover': { background: '#472c1a', color: '#f0dcc0' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
  },
  navRowMain: { display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 },
  navRowValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#f5e6c8',
    textTransform: 'capitalize',
  },
  navRowHint: {
    fontSize: '12.5px',
    color: '#a8875d',
  },
  navRowSide: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  // The two numbers a player actually asks about, without the click.
  navRowStat: {
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '0.06em',
    color: '#8a6e4c',
    whiteSpace: 'nowrap',
    '@media (max-width: 460px)': { display: 'none' },
  },
  chevron: { fontSize: '18px', color: '#c4a070', lineHeight: 1 },
  plateBtn: {
    width: '100%',
    minHeight: '48px',
    background: 'none',
    border: '1px solid #6b4622',
    borderRadius: '9px',
    color: '#d99a6a',
    padding: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: 'inherit',
    '&:hover': { background: 'rgba(217,79,43,0.08)' },
    '&:active': { background: 'rgba(217,79,43,0.12)' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
  },
  footer: {
    margin: 0,
    paddingTop: '4px',
    fontFamily: MONO,
    fontSize: '10.5px',
    letterSpacing: '0.08em',
    color: '#6f5a3f',
  },
});

function SettingsModal({ open, onClose, nickname, onNicknameChange, isHost,
  onResetGame, onOpenShortcuts, mode, settings, onOpenModes,
  access, onOpenRoomAccess,
  layoutPref = 'auto', autoLayout = 'desktop', onLayoutPrefChange }) {
  const classes = useStyles();
  const t = useT();
  const [localName, setLocalName] = useState(nickname);

  useModalEscape(open, onClose);

  useEffect(() => {
    setLocalName(nickname);
  }, [nickname, open]);

  const save = () => {
    if (localName !== nickname) {
      onNicknameChange(localName);
    }
  };

  if (!open) return null;

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div
        className={classes.modal}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('game.settings')}
      >
        <div className={classes.head}>
          <h2 className={classes.title}>{t('game.settings')}</h2>
          <button className={classes.closeBtn} onClick={onClose} aria-label={t('a11y.close')}>✕</button>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t('settings.profile')}</div>
          <input
            className={classes.nicknameInput}
            type="text"
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            onBlur={save}
            onKeyDown={e => e.key === 'Enter' && save()}
            placeholder={t('settings.yourName')}
            spellCheck={false}
            autoComplete="off"
            aria-label={t('menu.nickname')}
          />
        </div>

        {mode && onOpenModes && (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>{t('settings.gameMode')}</div>
            <button
              type="button"
              className={classes.navRow}
              onClick={onOpenModes}
              aria-label={t('a11y.gameMode', { mode })}
            >
              <span className={classes.navRowMain}>
                <span className={classes.navRowValue}>{mode}</span>
                <span className={classes.navRowHint}>{t(MODE_BLURBS[mode])}</span>
              </span>
              <span className={classes.navRowSide}>
                {settings && (
                  <span className={classes.navRowStat}>
                    {t('settings.modeStat', {
                      bid: settings.biddingTime, demo: settings.demoTime,
                    })}
                  </span>
                )}
                <span className={classes.chevron} aria-hidden="true">›</span>
              </span>
            </button>
          </div>
        )}

        {access && onOpenRoomAccess && (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>{t('access.roomAccess')}</div>
            <button
              type="button"
              className={classes.navRow}
              onClick={onOpenRoomAccess}
              aria-label={t('a11y.roomAccessState', {
                visibility: t(access.visibility === 'password' ? 'access.password' : 'access.public'),
                door: t(access.joinable ? 'a11y.acceptingNewPlayers' : 'a11y.notAcceptingNewPlayers'),
              })}
            >
              <span className={classes.navRowMain}>
                <span className={classes.navRowValue}>
                  {t(access.visibility === 'password' ? 'access.password' : 'access.public')}
                </span>
                <span className={classes.navRowHint}>
                  {t(access.joinable ? 'access.acceptingNewPlayers' : 'access.notAcceptingNewPlayers')}
                </span>
              </span>
              <span className={classes.navRowSide}>
                {access.visibility === 'password' && (
                  <span className={classes.navRowStat} aria-hidden="true">🔒</span>
                )}
                <span className={classes.chevron} aria-hidden="true">›</span>
              </span>
            </button>
          </div>
        )}

        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t('settings.language')}</div>
          <LanguagePicker />
        </div>

        {onLayoutPrefChange && (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>{t('settings.display')}</div>
            <LayoutPicker
              pref={layoutPref}
              autoLayout={autoLayout}
              onChoose={onLayoutPrefChange}
            />
          </div>
        )}

        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t('settings.howToPlay')}</div>
          <p className={classes.text}>{t('settings.howToPlayShort')}</p>
          {onOpenShortcuts && (
            <button className={classes.plateBtn} onClick={onOpenShortcuts}>
              {t('shortcuts.title')}
            </button>
          )}
        </div>

        {isHost && (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>{t('settings.room')}</div>
            {isHost && (
              <button
                className={classes.plateBtn}
                onClick={() => { if (window.confirm(t('settings.resetConfirm'))) { onResetGame?.(); onClose(); } }}
              >
                {t('settings.resetRoom')}
              </button>
            )}
          </div>
        )}

        <p className={classes.footer}>{t('settings.footer', { version: APP_VERSION })}</p>
      </div>
    </div>
  );
}

export default SettingsModal;
